/**
 * Shared overlay lifecycle helpers — focus save/restore, ESC key binding.
 *
 * Centralizes the safeAppCall('setLastFocused') / ('restoreLastFocused')
 * pattern and keyup ESC binding used by <cart-drawer>,
 * <newsletter-subscribe>, and collection-overlay. All three had identical
 * inline copies; one file means one place to update when the IIFE app
 * helpers migrate in Phase 13.
 *
 * bindEscape / unbindEscape use a WeakMap so element GC is not blocked
 * by the handler reference, and unbindEscape on an element that never had
 * a handler is a safe no-op.
 *
 * Source path: theme/js/core/overlay.esm.js
 * Built name : ./overlay.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Sonnet 4.6
 *
 * [Overlay]
 */

import { log } from './log.js';
import { safeAppCall } from './app-get.js';

// app.setLastFocused (application.esm.js) takes no arguments — it always
// reads `$(':focus')` internally. The wrapper is parameterless to match,
// so callers don't have a misleading "pass an element to save" affordance.
export function saveLastFocused() {
  safeAppCall('setLastFocused');
}

export function restoreLastFocused(delay = 0) {
  setTimeout(() => safeAppCall('restoreLastFocused'), delay);
}

const escHandlers = new WeakMap();

export function bindEscape(el, callback) {
  unbindEscape(el);
  const handler = (event) => {
    if (event.key === 'Escape' || event.keyCode === 27) callback();
  };
  escHandlers.set(el, handler);
  el.addEventListener('keyup', handler);
}

export function unbindEscape(el) {
  const handler = escHandlers.get(el);
  if (handler) {
    el.removeEventListener('keyup', handler);
    escHandlers.delete(el);
  }
}

log('[Overlay] loaded');
